/**
 * Patient Vault -- on-device encrypted health record storage.
 * Uses Web Crypto API for key generation and AES-GCM encryption.
 * Keys never leave the device.
 *
 * Security architecture:
 * - AES-GCM-256 for record encryption (authenticated encryption)
 * - PBKDF2 (600 000 iterations, SHA-256) for password-based key derivation
 * - Unique 12-byte IV per encryption operation (never reused with the same key)
 * - IndexedDB for persistence (same-origin policy enforced by the browser)
 * - Key wrapping via AES-KW for backup export
 */

// ---------------------------------------------------------------------------
// BIP39-style wordlist (200-word subset for seed phrase generation)
// ---------------------------------------------------------------------------
const WORDLIST: string[] = [
  'abandon','ability','able','about','above','absent','absorb','abstract','absurd','abuse',
  'access','accident','account','accuse','achieve','acid','acoustic','acquire','across','act',
  'action','actor','actual','adapt','add','addict','address','adjust','admit','adult',
  'advance','advice','aerobic','affair','afford','afraid','again','agent','agree','ahead',
  'aim','air','airport','aisle','alarm','album','alcohol','alert','alien','all',
  'alley','allow','almost','alone','alpha','already','also','alter','always','amateur',
  'amazing','among','amount','amused','anchor','ancient','anger','angle','angry','animal',
  'ankle','announce','annual','another','answer','antenna','antique','anxiety','any','apart',
  'apology','appear','apple','approve','april','arch','arctic','area','arena','argue',
  'arm','armor','army','arrange','arrest','arrive','arrow','art','artist','artwork',
  'ask','aspect','assault','asset','assist','assume','asthma','athlete','atom','attack',
  'attend','auction','audit','august','aunt','author','auto','autumn','average','avocado',
  'avoid','awake','aware','awesome','awful','awkward','axis','baby','bachelor','bacon',
  'badge','bag','balance','balcony','ball','bamboo','banana','banner','bar','barely',
  'bargain','barrel','base','basic','basket','battle','beach','bean','beauty','because',
  'become','beef','before','begin','behave','behind','believe','below','bench','benefit',
  'best','betray','better','between','beyond','bicycle','bid','bike','bind','biology',
  'bird','birth','bitter','black','blade','blame','blanket','blast','bleak','bless',
  'blind','blood','blossom','blue','blur','blush','board','boat','body','boil',
  'bomb','bone','bonus','book','boost','border','boring','borrow','boss','bottom',
];

// ---------------------------------------------------------------------------
// IndexedDB helpers (zero-dependency)
// ---------------------------------------------------------------------------
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(PatientVault.DB_NAME, PatientVault.DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(PatientVault.KEY_STORE)) {
        db.createObjectStore(PatientVault.KEY_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(PatientVault.RECORD_STORE)) {
        db.createObjectStore(PatientVault.RECORD_STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(storeName: string, value: unknown): Promise<void> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        tx.objectStore(storeName).put(value);
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      }),
  );
}

function idbGet<T>(storeName: string, key: string): Promise<T | undefined> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readonly');
        const req = tx.objectStore(storeName).get(key);
        req.onsuccess = () => {
          db.close();
          resolve(req.result as T | undefined);
        };
        req.onerror = () => {
          db.close();
          reject(req.error);
        };
      }),
  );
}

function idbGetAll<T>(storeName: string): Promise<T[]> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readonly');
        const req = tx.objectStore(storeName).getAll();
        req.onsuccess = () => {
          db.close();
          resolve(req.result as T[]);
        };
        req.onerror = () => {
          db.close();
          reject(req.error);
        };
      }),
  );
}

function idbDelete(storeName: string, key: string): Promise<void> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        tx.objectStore(storeName).delete(key);
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      }),
  );
}

function idbClear(storeName: string): Promise<void> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        tx.objectStore(storeName).clear();
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      }),
  );
}

// ---------------------------------------------------------------------------
// Encoding helpers
// ---------------------------------------------------------------------------
const encoder = new TextEncoder();
const decoder = new TextDecoder();

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// ---------------------------------------------------------------------------
// Internal types for IndexedDB records
// ---------------------------------------------------------------------------
interface StoredEncryptedRecord {
  id: string;
  ciphertext: string; // base64
  iv: string; // base64
  storedAt: string; // ISO timestamp
  type: string;
}

/** Shape of a wrapped key record stored in IndexedDB (CRIT-3 secure storage). */
interface StoredWrappedKeyRecord {
  id: string;
  wrappedKey: string; // base64-encoded AES-KW wrapped key
  salt: string; // base64-encoded wrapping salt
}

// ---------------------------------------------------------------------------
// PatientVault
// ---------------------------------------------------------------------------
export class PatientVault {
  static readonly DB_NAME = 'kaimed-patient-vault';
  static readonly DB_VERSION = 1;
  static readonly KEY_STORE = 'encryption-keys';
  static readonly RECORD_STORE = 'encrypted-records';

  // PBKDF2 iteration count -- OWASP recommends >= 600 000 for SHA-256
  private static readonly PBKDF2_ITERATIONS = 600_000;

  /** Known plaintext stored encrypted during init to verify seed-phrase-derived keys. */
  static readonly VERIFICATION_TOKEN = 'kaimed-vault-verification-token';
  static readonly VERIFICATION_RECORD_ID = '__vault-verification__';

  /**
   * Generate a new AES-GCM-256 key for the patient.
   * The key is extractable so it can be exported for backup.
   */
  static async generateKey(): Promise<CryptoKey> {
    return crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true, // extractable for backup
      ['encrypt', 'decrypt'],
    );
  }

  /**
   * Derive a PBKDF2 wrapping key from a password.
   * Returns an AES-KW key used to wrap/unwrap the vault key.
   */
  private static async deriveWrappingKey(
    password: string,
    salt: Uint8Array,
  ): Promise<CryptoKey> {
    const baseKey = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveKey'],
    );
    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: PatientVault.PBKDF2_ITERATIONS,
        hash: 'SHA-256',
      },
      baseKey,
      { name: 'AES-KW', length: 256 },
      false,
      ['wrapKey', 'unwrapKey'],
    );
  }

  /**
   * Export key as a password-protected backup string.
   * Format: base64(salt) + '.' + base64(wrappedKey)
   */
  static async exportKeyForBackup(
    key: CryptoKey,
    password: string,
  ): Promise<string> {
    if (!password || password.length < 8) {
      throw new Error('Backup password must be at least 8 characters');
    }
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const wrappingKey = await PatientVault.deriveWrappingKey(password, salt);
    const wrapped = await crypto.subtle.wrapKey('raw', key, wrappingKey, 'AES-KW');
    return `${arrayBufferToBase64(salt.buffer)}.${arrayBufferToBase64(wrapped)}`;
  }

  /**
   * Import key from a backup string produced by exportKeyForBackup.
   */
  static async importKeyFromBackup(
    encryptedKey: string,
    password: string,
  ): Promise<CryptoKey> {
    const parts = encryptedKey.split('.');
    if (parts.length !== 2) {
      throw new Error('Invalid backup format');
    }
    const salt = new Uint8Array(base64ToArrayBuffer(parts[0]));
    const wrappedKey = base64ToArrayBuffer(parts[1]);
    const wrappingKey = await PatientVault.deriveWrappingKey(password, salt);
    return crypto.subtle.unwrapKey(
      'raw',
      wrappedKey,
      wrappingKey,
      'AES-KW',
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt'],
    );
  }

  /**
   * Encrypt a health record.
   * Generates a unique 12-byte IV for each operation.
   */
  static async encrypt(
    key: CryptoKey,
    data: unknown,
  ): Promise<{ ciphertext: ArrayBuffer; iv: Uint8Array }> {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const plaintext = encoder.encode(JSON.stringify(data));
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      plaintext,
    );
    return { ciphertext, iv };
  }

  /**
   * Decrypt a health record.
   */
  static async decrypt(
    key: CryptoKey,
    ciphertext: ArrayBuffer,
    iv: Uint8Array,
  ): Promise<unknown> {
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      ciphertext,
    );
    return JSON.parse(decoder.decode(plaintext));
  }

  /**
   * Store an encrypted record in IndexedDB.
   * @param recordId Unique identifier for the record.
   * @param encryptedData Output of encrypt().
   * @param recordType Human-readable type (e.g. "lab-result", "prescription").
   */
  static async storeRecord(
    recordId: string,
    encryptedData: { ciphertext: ArrayBuffer; iv: Uint8Array },
    recordType: string = 'general',
  ): Promise<void> {
    const record: StoredEncryptedRecord = {
      id: recordId,
      ciphertext: arrayBufferToBase64(encryptedData.ciphertext),
      iv: arrayBufferToBase64(encryptedData.iv.buffer),
      storedAt: new Date().toISOString(),
      type: recordType,
    };
    await idbPut(PatientVault.RECORD_STORE, record);
  }

  /**
   * Retrieve and decrypt a record from IndexedDB.
   */
  static async getRecord(key: CryptoKey, recordId: string): Promise<unknown | null> {
    const stored = await idbGet<StoredEncryptedRecord>(PatientVault.RECORD_STORE, recordId);
    if (!stored) return null;
    const ciphertext = base64ToArrayBuffer(stored.ciphertext);
    const iv = new Uint8Array(base64ToArrayBuffer(stored.iv));
    return PatientVault.decrypt(key, ciphertext, iv);
  }

  /**
   * List all record IDs with metadata (no decrypted content).
   */
  static async listRecords(): Promise<{ id: string; storedAt: string; type: string }[]> {
    const all = await idbGetAll<StoredEncryptedRecord>(PatientVault.RECORD_STORE);
    return all.map((r) => ({ id: r.id, storedAt: r.storedAt, type: r.type }));
  }

  /**
   * Delete a single record from IndexedDB.
   */
  static async deleteRecord(recordId: string): Promise<void> {
    await idbDelete(PatientVault.RECORD_STORE, recordId);
  }

  /**
   * Derive a device-bound wrapping key for protecting the master key at rest.
   * Uses PBKDF2 with a random salt and device-specific entropy so the wrapped
   * key is tied to this origin/browser combination.
   */
  private static async deriveStorageWrappingKey(
    salt: Uint8Array,
  ): Promise<CryptoKey> {
    // Use origin + userAgent as device-specific entropy. Not cryptographically
    // perfect, but significantly better than storing plaintext JWK in IndexedDB.
    const deviceEntropy =
      (typeof navigator !== 'undefined' ? navigator.userAgent : '') +
      (typeof window !== 'undefined' ? window.location.origin : '');
    const baseKey = await crypto.subtle.importKey(
      'raw',
      encoder.encode(deviceEntropy),
      'PBKDF2',
      false,
      ['deriveKey'],
    );
    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: PatientVault.PBKDF2_ITERATIONS,
        hash: 'SHA-256',
      },
      baseKey,
      { name: 'AES-KW', length: 256 },
      false,
      ['wrapKey', 'unwrapKey'],
    );
  }

  /**
   * Store the encryption key in IndexedDB.
   * The key is wrapped with a device-derived AES-KW key before storage so
   * it is never persisted as a plaintext JWK.
   */
  static async storeKey(key: CryptoKey): Promise<void> {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const wrappingKey = await PatientVault.deriveStorageWrappingKey(salt);
    const wrapped = await crypto.subtle.wrapKey('raw', key, wrappingKey, 'AES-KW');
    const record: StoredWrappedKeyRecord = {
      id: 'vault-master-key',
      wrappedKey: arrayBufferToBase64(wrapped),
      salt: arrayBufferToBase64(salt.buffer),
    };
    await idbPut(PatientVault.KEY_STORE, record);
  }

  /**
   * Retrieve the stored key from IndexedDB.
   * Unwraps the key using the device-derived wrapping key and returns a
   * non-extractable CryptoKey suitable only for encrypt/decrypt operations.
   */
  static async getStoredKey(): Promise<CryptoKey | null> {
    const stored = await idbGet<StoredWrappedKeyRecord>(
      PatientVault.KEY_STORE,
      'vault-master-key',
    );
    if (!stored || !stored.wrappedKey || !stored.salt) return null;
    const salt = new Uint8Array(base64ToArrayBuffer(stored.salt));
    const wrappingKey = await PatientVault.deriveStorageWrappingKey(salt);
    const wrappedKey = base64ToArrayBuffer(stored.wrappedKey);
    return crypto.subtle.unwrapKey(
      'raw',
      wrappedKey,
      wrappingKey,
      'AES-KW',
      { name: 'AES-GCM', length: 256 },
      false, // non-extractable for runtime use
      ['encrypt', 'decrypt'],
    );
  }

  /**
   * Generate a 12-word seed phrase using cryptographically secure randomness.
   * Each word is selected from the 200-word subset using 8 bits of entropy per word.
   */
  static generateSeedPhrase(): string {
    const entropy = crypto.getRandomValues(new Uint8Array(12));
    const words = Array.from(entropy).map(
      (byte) => WORDLIST[byte % WORDLIST.length],
    );
    return words.join(' ');
  }

  /**
   * Derive an AES-GCM-256 key from a seed phrase using PBKDF2.
   * The seed phrase acts as the password; a fixed domain-separation salt is used.
   */
  static async deriveKeyFromSeed(seedPhrase: string): Promise<CryptoKey> {
    const normalized = seedPhrase.trim().toLowerCase().replace(/\s+/g, ' ');
    // Domain-separation salt -- fixed per application so the same seed always
    // produces the same key. This is safe because the seed phrase itself
    // provides the entropy.
    const salt = encoder.encode('kaimed-patient-vault-seed-v1');
    const baseKey = await crypto.subtle.importKey(
      'raw',
      encoder.encode(normalized),
      'PBKDF2',
      false,
      ['deriveKey'],
    );
    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: PatientVault.PBKDF2_ITERATIONS,
        hash: 'SHA-256',
      },
      baseKey,
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt'],
    );
  }

  /**
   * Check if the vault has been initialized (a master key exists).
   */
  static async isInitialized(): Promise<boolean> {
    const key = await PatientVault.getStoredKey();
    return key !== null;
  }

  /**
   * Clear the entire vault -- deletes all keys and encrypted records.
   * This is irreversible; callers must confirm with the user first.
   */
  static async clearVault(): Promise<void> {
    await idbClear(PatientVault.KEY_STORE);
    await idbClear(PatientVault.RECORD_STORE);
  }

  /**
   * Store a verification token encrypted with the given key.
   * Called during vault initialization so that seed phrase recovery can
   * verify the derived key before overwriting the master.
   */
  static async storeVerificationToken(key: CryptoKey): Promise<void> {
    const encrypted = await PatientVault.encrypt(key, PatientVault.VERIFICATION_TOKEN);
    await PatientVault.storeRecord(
      PatientVault.VERIFICATION_RECORD_ID,
      encrypted,
      'verification',
    );
  }

  /**
   * Verify that a candidate key can decrypt the verification token.
   * Returns true if the decrypted value matches the known plaintext.
   */
  static async verifyKey(candidateKey: CryptoKey): Promise<boolean> {
    try {
      const decrypted = await PatientVault.getRecord(
        candidateKey,
        PatientVault.VERIFICATION_RECORD_ID,
      );
      return decrypted === PatientVault.VERIFICATION_TOKEN;
    } catch {
      // Decryption failure means wrong key
      return false;
    }
  }
}
